import { useQuery } from "@tanstack/react-query";
import { useState, useCallback } from "react";

import type { GoogleCalendarEvent, ProcessedEvent } from "@/types/calendar";

const GOOGLE_API_KEY = import.meta.env.VITE_GOOGLE_CALENDAR_API_KEY;
const CALENDAR_ID = import.meta.env.VITE_GOOGLE_CALENDAR_ID;

// Helper to convert Google Calendar date formats to JavaScript Date objects
const parseGoogleDate = (
  dateObj: GoogleCalendarEvent["start"] | GoogleCalendarEvent["end"],
): Date | null => {
  if (!dateObj) {
    console.warn("Date object is null or undefined");
    return null;
  }

  try {
    if (dateObj.dateTime) {
      // Handle datetime with timezone
      const date = new Date(dateObj.dateTime);
      if (isNaN(date.getTime())) {
        console.warn("Failed to parse datetime:", dateObj.dateTime);
        return null;
      }
      return date;
    } else if (dateObj.date) {
      // Handle all-day events by using UTC to avoid timezone issues
      const date = new Date(dateObj.date);
      if (isNaN(date.getTime())) {
        console.warn("Failed to parse date:", dateObj.date);
        return null;
      }
      return date;
    } else {
      console.warn("Date object has neither dateTime nor date property");
      return null;
    }
  } catch (error) {
    console.error("Error parsing date:", error, "Date object:", dateObj);
    return null;
  }
};

// Calculate a "details score" for an event to determine which version has more information
const calculateDetailsScore = (item: GoogleCalendarEvent): number => {
  let score = 0;

  // Award points for having detailed information
  if (item.description && item.description.trim().length > 0) score += 10;
  if (item.location && item.location.trim().length > 0) score += 5;
  if (item.hangoutLink) score += 3;
  if (item.conferenceData?.conferenceId) score += 3;
  if (item.attendees && item.attendees.length > 0) score += 2;
  if (item.organizer) score += 1;
  if (item.creator) score += 1;

  return score;
};

// Process a single Google Calendar event
const processGoogleEvent = (
  item: GoogleCalendarEvent,
): ProcessedEvent | null => {
  try {
    const start = parseGoogleDate(item.start);
    const end = parseGoogleDate(item.end);

    // Skip events with invalid dates
    if (!start || !end) {
      console.warn(`Skipping event ${item.id} due to invalid dates`);
      return null;
    }

    // 🔍 DEBUG: Log Blueprint AI events with their recurring event IDs
    if (item.summary?.toLowerCase().includes("blueprint")) {
      console.log("🎯 Blueprint AI Event Found:", {
        title: item.summary,
        eventId: item.id,
        recurringEventId: item.recurringEventId || "NOT A RECURRING EVENT",
        organizer: item.organizer?.email,
      });
    }

    return {
      id: item.id,
      title: item.summary || "Untitled Event",
      start,
      end,
      description: item.description || "",
      location: item.location || "",
      meetingLink: item.hangoutLink || "",
      meetingId: item.conferenceData?.conferenceId || "",
      meetingPassword: item.conferenceData?.password || "",
      attendees:
        item.attendees?.map((a) => ({
          email: a.email,
          displayName: a.displayName || a.email,
          organizer: !!a.organizer,
          responseStatus: a.responseStatus || "needsAction",
        })) || [],
      recurringEventId: item.recurringEventId, // Extract recurring event ID
      resource: item,
    };
  } catch (error) {
    console.error(`Error processing event ${item.id}:`, error);
    return null;
  }
};

export const useCalendarEvents = () => {
  const [error, setError] = useState<Error | null>(null);

  const fetchEvents = useCallback(async () => {
    // Return empty array if calendar is not configured
    if (!GOOGLE_API_KEY || !CALENDAR_ID) {
      console.info(
        "Google Calendar not configured - calendar features disabled",
      );
      return [];
    }

    try {
      // Add parameters to reduce duplicates and improve data quality
      const params = new URLSearchParams({
        key: GOOGLE_API_KEY,
        singleEvents: "true", // Expand recurring events into individual instances
        orderBy: "startTime", // Order by start time
        maxResults: "250", // Limit results to avoid too many events
        timeMin: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(), // Events from 30 days ago
        timeMax: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(), // Events up to 1 year ahead
      });

      const response = await fetch(
        `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(
          CALENDAR_ID,
        )}/events?${params.toString()}`,
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          `Failed to fetch events: ${errorData.error?.message || response.statusText}`,
        );
      }

      const data = await response.json();
      return data.items as GoogleCalendarEvent[];
    } catch (error) {
      console.error("Error fetching Google Calendar events:", error);
      return []; // Return empty array on error
    }
  }, []);

  const processEvents = useCallback(
    (items: GoogleCalendarEvent[]): ProcessedEvent[] => {
      const eventMap = new Map<
        string,
        { event: GoogleCalendarEvent; score: number }
      >();
      const processedEvents: ProcessedEvent[] = [];
      let errorCount = 0;
      let duplicateCount = 0;

      // First pass: Group events by ID and keep the one with the highest details score
      for (const item of items) {
        const eventId = item.id;
        const detailsScore = calculateDetailsScore(item);

        const existing = eventMap.get(eventId);
        if (!existing || detailsScore > existing.score) {
          if (existing) {
            duplicateCount++;
            console.log(
              `Replacing duplicate event ${eventId} (old score: ${existing.score}, new score: ${detailsScore})`,
            );
          }
          eventMap.set(eventId, { event: item, score: detailsScore });
        } else {
          duplicateCount++;
          console.log(
            `Skipping duplicate event ${eventId} (current score: ${existing.score}, new score: ${detailsScore})`,
          );
        }
      }

      // Second pass: Process the deduplicated events
      for (const { event } of eventMap.values()) {
        const processedEvent = processGoogleEvent(event);
        if (processedEvent) {
          processedEvents.push(processedEvent);
        } else {
          errorCount++;
        }
      }

      if (duplicateCount > 0) {
        console.info(
          `Removed ${duplicateCount} duplicate events, keeping versions with more details`,
        );
      }

      if (errorCount > 0) {
        console.warn(
          `${errorCount} events were skipped due to processing errors`,
        );
        setError(new Error(`${errorCount} events failed to process`));
      }

      // Sort events by start time
      return processedEvents.sort(
        (a, b) => a.start.getTime() - b.start.getTime(),
      );
    },
    [],
  );

  const {
    data: events,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["calendar-events"],
    queryFn: async () => {
      const items = await fetchEvents();
      return processEvents(items);
    },
    retry: 2,
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
    refetchOnWindowFocus: true,
  });

  return {
    events: events || [],
    isLoading,
    isError,
    error,
    refetch,
    isCalendarEnabled: !!(GOOGLE_API_KEY && CALENDAR_ID),
  };
};
